import { Button } from '@/components/ui/button';
import { useInputAmount } from '@/hooks/state/useInputAmount';
import { formatDecimal } from '@/lib/format';
import { cn, isValidEvmAddress, isValidSolanaAddress } from '@/lib/utils';
import { useBoomWallet } from '@boomeme/wallet-sdk';
import BigNumber from 'bignumber.js';
import { X as Closed, InfoIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { useAppStore } from '@/store';
import { useAppEventBusContext } from '@/context/eventBusContext';
import { SubscribeEvent } from '@/interface/event';

export default () => {
  const { walletAddress, transactions, chain, evmTransactions } = useBoomWallet();
  const { fwClass, chainInfo, chainCoinBalance, chainCoinPrice, withdrawOpen, setWithdrawOpen, lang } = useAppStore();
  const { t } = useTranslation();
  const { publish } = useAppEventBusContext();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { inputAmount, onBlurInputAmount, onChangeInputAmount, setInputAmount } = useInputAmount('');
  const [inputAddress, setInputAddress] = useState('');

  const inputAmountPrice = BigNumber(inputAmount).times(chainCoinPrice).toString();

  const isvalidAddress = chain == 'solana' ? isValidSolanaAddress(inputAddress) : isValidEvmAddress(inputAddress);

  const isDisabled = !isvalidAddress || inputAmount == '' || BigNumber(inputAmount).gt(chainCoinBalance) || BigNumber(inputAmount).eq(0);

  const transfer = async () => {
    if (!walletAddress) return;
    if (chain == 'solana') {
      const res = await transactions.transferSolana(walletAddress, inputAddress, Number(inputAmount));
      if (res) {
        clearAll();
      }
    } else {
      const res = await evmTransactions.transferNativeToken(inputAddress, Number(inputAmount));
      if (res) {
        clearAll();
        publish(SubscribeEvent.Update_Balance);
      }
    }
  };

  const clearAll = () => {
    setInputAmount('');
    setInputAddress('');
    setConfirmOpen(false);
    setWithdrawOpen(false);
  };

  const AddressTips = useMemo(() => {
    if (inputAddress == '') return () => null;
    return () => (isvalidAddress ? null : <p className="text-sellColor text-[13px]">{t('Base.invalidAddressTips')}</p>);
  }, [inputAddress, isvalidAddress, lang]);

  return (
    <>
      <Sheet open={withdrawOpen} onOpenChange={clearAll}>
        <SheetContent
          side="bottom"
          hideCloseIcon
          className="w-full overflow-y-auto p-[16px] top-[100px] border-t-[1px] border-t-borderColor flex flex-col justify-between"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div>
            <SheetHeader>
              <SheetTitle>
                <div className="flex justify-between mb-[16px]">
                  <span>{t('Base.withdrawFund')}</span>
                  <Closed className=" absolute top-[15px] right-[15px]" onClick={clearAll} />
                </div>
              </SheetTitle>
            </SheetHeader>
            <div className="flex flex-col gap-y-[16px]">
              <div className="bg-[#F4F4F5CC] dark:bg-borderColor border-[1px] border-borderColor px-[12px] py-[8px] rounded-[12px]">
                <div className="flex items-center justify-between mb-[4px]">
                  <Input
                    type="text"
                    className="w-[50%] bg-[unset] px-0 outline-none shadow-none border-none"
                    placeholder={t('Base.enterAmount')}
                    inputMode="decimal"
                    value={inputAmount}
                    onChange={onChangeInputAmount}
                    onBlur={onBlurInputAmount}
                  />
                  <div className="flex items-center gap-x-[10px] text-[14px]">
                    <span className="text-buyColor flex-shrink-0" onClick={() => setInputAmount(formatDecimal(chainCoinBalance, 4))}>
                      {t('Base.all')}
                    </span>
                    <div className="flex items-center gap-x-[6px]">
                      <img src={chainInfo.icon} className="size-[18px]" />
                      <span>{chainInfo.symbol}</span>
                    </div>
                  </div>
                </div>
                <div className="text-secondaryTextColor flex justify-between text-[12px]">
                  <span>≈ ${formatDecimal(inputAmountPrice)}</span>
                  <div onClick={() => setInputAmount(formatDecimal(chainCoinBalance))}>
                    {t('Base.available')}&nbsp;
                    <span className="text-primaryTextColor">{formatDecimal(chainCoinBalance, 4)}</span> {chainInfo.symbol}
                  </div>
                </div>
              </div>
              <Input
                className={cn(
                  'px-[12px] outline-none shadow-none py-[8px] h-[60px] bg-[#F4F4F5CC] dark:bg-borderColor border-[1px] border-borderColor rounded-[12px]',
                  inputAddress.length > 0 ? (isvalidAddress ? '' : 'border-[1px] !border-sellColor') : '',
                )}
                placeholder={t('Base.enterAddress')}
                value={inputAddress}
                onChange={(e) => setInputAddress(e.target.value)}
              />
              <AddressTips />
              <div className="text-base">
                <InfoIcon className="w-[16px] h-[16px] inline align-text-bottom text-secondaryTextColor" />
                <span className="text-[13px] text-secondaryTextColor pl-[5px]">{t('Base.withdrawTips')}</span>
              </div>
            </div>
          </div>
          <div className={'flex flex-col gap-y-[11px] text-[14px]'}>
            <Button
              className="w-full h-[48px] mb !bg-themeColor rounded-[12px] text-[14px] text-primaryTextColor"
              onClick={() => setConfirmOpen(true)}
              disabled={isDisabled}
            >
              <span className={fwClass}>{t('Base.withdraw')}</span>
            </Button>
            <Button
              variant="secondary"
              className={cn('w-full h-[48px] rounded-[12px]  !text-primaryTextColor ', fwClass)}
              onClick={() => setWithdrawOpen(false)}
            >
              {t('Base.cancel')}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
      <Sheet open={confirmOpen} onOpenChange={setConfirmOpen}>
        <SheetContent
          side="bottom"
          hideCloseIcon
          className="w-full overflow-y-auto p-[16px] top-[100px] border-t-[1px] border-t-borderColor flex flex-col justify-between"
        >
          <div>
            <SheetHeader>
              <SheetTitle>
                <div className="flex justify-between items-center mb-[16px]">
                  <span>{t('Base.withdrawFund')}</span>
                  <Closed className=" absolute top-[15px] right-[15px]" onClick={() => setConfirmOpen(false)} />
                </div>
              </SheetTitle>
            </SheetHeader>
            <div className="flex flex-col gap-y-[16px]">
              <p className={cn('text-left text-secondaryTextColor text-[16px]', fwClass)}>
                <Trans
                  i18nKey={'Base.sendTips'}
                  components={[<span className="text-primaryTextColor"></span>]}
                  values={{
                    amount: inputAmount,
                    symbol: chainInfo.symbol,
                  }}
                />
              </p>
              <p className="bg-cardBgColor rounded-[8px] px-[10px] py-[6px] break-all text-left text-[14px]">{inputAddress}</p>
              <div className="text-base">
                <InfoIcon className="w-[16px] h-[16px] inline align-text-bottom text-secondaryTextColor" />
                <span className="text-[13px] text-secondaryTextColor pl-[5px]">{t('Base.withdrawTips')}</span>
              </div>
            </div>
          </div>

          <div className={'flex flex-col gap-y-[11px] text-[14px]'}>
            <Button className="w-full !bg-themeColor rounded-[12px] h-[48px] text-[#09090B]" onClick={transfer}>
              <span className={fwClass}>{t('Base.confirmWithdraw')}</span>
            </Button>
            <Button variant="secondary" className={cn('w-full h-[48px] rounded-[12px]', fwClass)} onClick={() => setConfirmOpen(false)}>
              {t('Base.cancel')}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
